import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';

async function sha256Hex(input) {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function randomTokenHex() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function buildFeedUrl(rawToken) {
  const base = process.env.REACT_APP_SUPABASE_URL || '';
  if (!base) return '';
  return `${base.replace(/\/$/, '')}/functions/v1/generate-ics-feed?token=${rawToken}`;
}

export const useCalendarSync = (colaboradorId) => {
  const [feeds, setFeeds] = useState([]);
  const [activeToken, setActiveToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const fetchFeeds = useCallback(async () => {
    if (!colaboradorId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('colaborador_calendar_feeds')
        .select('*')
        .eq('colaborador_id', colaboradorId)
        .order('created_at', { ascending: true });
      if (err) throw err;
      setFeeds(data || []);
    } catch (err) {
      console.error('fetchFeeds error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [colaboradorId]);

  const fetchActiveToken = useCallback(async () => {
    if (!colaboradorId) return;
    try {
      const { data, error: err } = await supabase
        .from('external_feed_tokens')
        .select('id, is_active, last_accessed_at, access_count, created_at')
        .eq('colaborador_id', colaboradorId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);
      if (err) throw err;
      setActiveToken(data?.[0] || null);
    } catch (err) {
      console.error('fetchActiveToken error:', err);
    }
  }, [colaboradorId]);

  useEffect(() => {
    fetchFeeds();
    fetchActiveToken();
  }, [fetchFeeds, fetchActiveToken]);

  const addFeed = useCallback(async ({ feedUrl, provider, calendarName }) => {
    setError(null);
    if (!colaboradorId) return { error: 'sem_colaborador' };
    if (!feedUrl?.trim() || !/^https?:\/\//i.test(feedUrl.trim())) {
      return { error: 'URL inválida — deve começar com http:// ou https://' };
    }
    const { data, error: err } = await supabase
      .from('colaborador_calendar_feeds')
      .insert({
        colaborador_id: colaboradorId,
        feed_url: feedUrl.trim(),
        provider: provider || 'other',
        calendar_name: calendarName?.trim() || null,
        is_active: true,
      })
      .select()
      .single();
    if (err) {
      console.error('addFeed error:', err);
      return { error: err.message };
    }
    await fetchFeeds();
    return { data };
  }, [colaboradorId, fetchFeeds]);

  const removeFeed = useCallback(async (feedId) => {
    setError(null);
    const { error: err } = await supabase
      .from('colaborador_calendar_feeds')
      .delete()
      .eq('id', feedId);
    if (err) {
      console.error('removeFeed error:', err);
      return { error: err.message };
    }
    await fetchFeeds();
    return {};
  }, [fetchFeeds]);

  const syncFeeds = useCallback(async () => {
    if (!colaboradorId) return { error: 'sem_colaborador' };
    setSyncing(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.functions.invoke('sync-external-calendars', {
        body: { colaborador_id: colaboradorId },
      });
      if (err) throw err;
      await fetchFeeds();
      return { data };
    } catch (err) {
      console.error('syncFeeds error:', err);
      return { error: err.message };
    } finally {
      setSyncing(false);
    }
  }, [colaboradorId, fetchFeeds]);

  const generateToken = useCallback(async () => {
    if (!colaboradorId) return { error: 'sem_colaborador' };
    setError(null);
    try {
      // Revoga tokens anteriores (um ativo por vez)
      await supabase
        .from('external_feed_tokens')
        .update({ is_active: false })
        .eq('colaborador_id', colaboradorId)
        .eq('is_active', true);

      const raw = randomTokenHex();
      const hash = await sha256Hex(raw);
      const { data, error: err } = await supabase
        .from('external_feed_tokens')
        .insert({
          colaborador_id: colaboradorId,
          token_hash: hash,
          is_active: true,
        })
        .select()
        .single();
      if (err) throw err;
      await fetchActiveToken();
      return { url: buildFeedUrl(raw), tokenId: data.id };
    } catch (err) {
      console.error('generateToken error:', err);
      return { error: err.message };
    }
  }, [colaboradorId, fetchActiveToken]);

  const revokeToken = useCallback(async () => {
    if (!colaboradorId) return { error: 'sem_colaborador' };
    const { error: err } = await supabase
      .from('external_feed_tokens')
      .update({ is_active: false })
      .eq('colaborador_id', colaboradorId)
      .eq('is_active', true);
    if (err) {
      console.error('revokeToken error:', err);
      return { error: err.message };
    }
    await fetchActiveToken();
    return {};
  }, [colaboradorId, fetchActiveToken]);

  return {
    feeds,
    activeToken,
    loading,
    syncing,
    error,
    fetchFeeds,
    fetchActiveToken,
    addFeed,
    removeFeed,
    syncFeeds,
    generateToken,
    revokeToken,
  };
};

export default useCalendarSync;
